/**
 * WellnessTable — data table for wellness reports with dynamic columns.
 *
 * Supports all 5 report types x 2 view modes = 10 column sets.
 * Reuses FilterPopover + ActiveFilterBar from residents components.
 *
 * Props:
 *   data            — paginated rows for current page
 *   allData         — full filtered dataset (for filter unique values)
 *   columnDefs      — active column definitions for current reportType + viewMode
 *   visibleColumns  — string[] of visible column keys
 *   sortColumn      — current sort column key
 *   sortOrder       — 'asc' | 'desc'
 *   onSort          — (columnKey) => void
 *   filters         — { columnKey: [values] }
 *   onFilterChange  — (columnKey, selectedValues) => void
 *   onClearFilters  — () => void
 *   showFilters     — boolean (true for detailed, false for summary)
 *   tzOffset        — timezone offset for datetime display
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoToLocal } from '../../../../libs.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import { getDeviceProfileLink } from '../../../../utils/deviceLinks.js';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

// ---------------------------------------------------------------------------
// Column definitions per report type + view mode
// ---------------------------------------------------------------------------

export const COLUMN_DEFS_MAP = {
    'check-in': {
        detailed: [
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true, required: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'device_name', label: 'Device Used', sortable: true, filterable: true },
            { key: 'window_date', label: 'Date', sortable: true, filterable: true },
            { key: 'window_start', label: 'Check In Start', sortable: true, filterable: false },
            { key: 'window_end', label: 'Check In End', sortable: true, filterable: false },
            { key: 'checkin_timestamp', label: 'Check In Time', sortable: true, filterable: false },
            { key: 'checked_in', label: 'Checked-in?', sortable: true, filterable: true },
        ],
        summary: [
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true, required: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'windows_count', label: 'Check-ins Required', sortable: true, filterable: false },
            { key: 'check_in_count', label: 'Successful Check-ins', sortable: true, filterable: false },
            { key: 'missed_checkin_count', label: 'Missed Check-ins', sortable: true, filterable: false },
            { key: 'local_last_check_in', label: 'Last Check-in', sortable: true, filterable: false },
        ],
    },
    'missed-check-in': {
        detailed: [
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true, required: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'resident_phone', label: 'Phone #', sortable: true, filterable: false },
            { key: 'window_start', label: 'Check In Start', sortable: true, filterable: false },
            { key: 'window_end', label: 'Check In End', sortable: true, filterable: false },
            { key: 'local_last_check_in', label: 'Last Check-in', sortable: true, filterable: false },
        ],
        summary: [
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true, required: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'resident_phone', label: 'Phone #', sortable: true, filterable: false },
            { key: 'windows_count', label: 'Check-ins Required', sortable: true, filterable: false },
            { key: 'missed_checkin_count', label: 'Failed Check-ins', sortable: true, filterable: false },
            { key: 'local_last_check_in', label: 'Last Check-in', sortable: true, filterable: false },
        ],
    },
    'check-in-history': {
        detailed: [
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true, required: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'device_name', label: 'Checked-in By', sortable: true, filterable: true },
            { key: 'local_create_time', label: 'Checked-in At', sortable: true, filterable: false },
        ],
        summary: [
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true, required: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'count', label: 'Check-in Count', sortable: true, filterable: false },
        ],
    },
    'rounds': {
        // NOTE: keys mirror the shared getRoundsReportV2Data backend payload,
        // which reuses the check-in field names (checkin_timestamp / checked_in /
        // check_in_count / missed_checkin_count). Classic Portal reads the same
        // keys, so they are consumed as-is here rather than renamed on the backend.
        detailed: [
            { key: 'area_name', label: 'Area', sortable: true, filterable: true, required: true },
            { key: 'device_name', label: 'Device', sortable: true, filterable: true },
            { key: 'window_start', label: 'Rounds Start', sortable: true, filterable: false },
            { key: 'window_end', label: 'Rounds End', sortable: true, filterable: false },
            { key: 'checkin_timestamp', label: 'Performed At', sortable: true, filterable: false },
            { key: 'checked_in', label: 'Rounds Performed?', sortable: true, filterable: true },
        ],
        summary: [
            { key: 'area_name', label: 'Area', sortable: true, filterable: true, required: true },
            { key: 'windows_count', label: 'Rounds to Perform', sortable: true, filterable: false },
            { key: 'check_in_count', label: '# Rounds Performed', sortable: true, filterable: false },
            { key: 'missed_checkin_count', label: '# Missed Rounds', sortable: true, filterable: false },
        ],
    },
    'rounds-history': {
        detailed: [
            { key: 'area_name', label: 'Area', sortable: true, filterable: true, required: true },
            { key: 'device_name', label: 'Device Used', sortable: true, filterable: true },
            { key: 'local_create_time', label: 'Performed At', sortable: true, filterable: false },
        ],
        summary: [
            { key: 'area_name', label: 'Area', sortable: true, filterable: true, required: true },
            { key: 'resident_name', label: 'Residents', sortable: true, filterable: true },
            { key: 'count', label: 'Rounds Count', sortable: true, filterable: false },
        ],
    },
};

// Build column labels map (for ActiveFilterBar)
function buildColumnLabels(columnDefs) {
    const labels = {};
    columnDefs.forEach((col) => { labels[col.key] = col.label; });
    return labels;
}

// ---------------------------------------------------------------------------
// Sort icon
// ---------------------------------------------------------------------------

const SortIcon = ({ order }) => (
    <svg style={{ width: '14px', height: '14px', verticalAlign: 'middle', marginLeft: '4px' }} viewBox="0 0 16 16" fill="currentColor">
        {order === 'asc' ? <path d="M8 4l4 5H4l4-5z" /> : <path d="M8 12l4-5H4l4 5z" />}
    </svg>
);

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

function formatDatetime(val) {
    if (!val) return '--';
    try {
        return convertIsoToLocal(val) || val;
    } catch {
        return val;
    }
}

function renderCell(row, colKey, tzOffset) {
    const val = row[colKey];

    switch (colKey) {
        case 'resident_name':
            return row.resident_id ? (
                <Link to={`/profiles/resident/${row.resident_id}`} className="ar-link">{val || '--'}</Link>
            ) : (val || '--');

        case 'area_name':
            return row.area_id ? (
                <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{val || '--'}</Link>
            ) : (val || '--');

        case 'device_name': {
            // The shared device rule. Note the check-in rows carry no manufacture_id
            // today, so this renders plain text until the proc returns it.
            const deviceLinkTo = getDeviceProfileLink(row);

            return (
                <div className="wr-stacked-cell">
                    {deviceLinkTo ? (
                        <Link to={deviceLinkTo} className="ar-link">{val || '--'}</Link>
                    ) : (
                        <span>{val || '--'}</span>
                    )}
                    {row.manufacture_uuid ? (
                        <span className="wr-stacked-cell__sub">{row.manufacture_uuid}</span>
                    ) : null}
                </div>
            );
        }

        case 'resident_phone':
            return val ? (
                <a href={`tel:${val}`} className="wr-phone-link">{val}</a>
            ) : '--';

        case 'checked_in':
        case 'rounds_performed': {
            const isYes = val === 1 || val === true || val === '1' || val === 'true' || val === 'Yes';
            return (
                <span className={`wr-bool ${isYes ? 'wr-bool--yes' : 'wr-bool--no'}`}>
                    <svg className="wr-bool__icon" viewBox="0 0 20 20" fill="currentColor">
                        {isYes
                            ? <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                            : <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L10 8.94l3.47-3.47a.75.75 0 1 1 1.06 1.06L11.06 10l3.47 3.47a.75.75 0 1 1-1.06 1.06L10 11.06l-3.47 3.47a.75.75 0 0 1-1.06-1.06L8.94 10 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />}
                    </svg>
                    {isYes ? 'Yes' : 'No'}
                </span>
            );
        }

        case 'checkin_timestamp':
        case 'local_create_time':
        case 'local_last_check_in':
            return formatDatetime(val);

        case 'windows_count':
        case 'check_in_count':
        case 'missed_checkin_count':
        case 'rounds_count':
        case 'missed_rounds_count':
        case 'count':
            return val != null ? Number(val).toLocaleString() : '0';

        default:
            return val != null ? String(val) : '--';
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WellnessTable = ({
    data,
    allData,
    columnDefs,
    visibleColumns,
    sortColumn,
    sortOrder,
    onSort,
    filters,
    onFilterChange,
    onClearFilters,
    showFilters = true,
    tzOffset = 0,
}) => {
    const columnLabels = useMemo(() => buildColumnLabels(columnDefs), [columnDefs]);

    const activeCols = useMemo(() => {
        return columnDefs.filter((col) => visibleColumns.includes(col.key));
    }, [columnDefs, visibleColumns]);

    const getFilterValues = useCallback((colKey) => {
        const vals = new Set();
        (allData || []).forEach((row) => {
            const v = row[colKey];
            if (v != null && v !== '') vals.add(String(v));
        });
        return [...vals].sort();
    }, [allData]);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        const current = filters[columnKey] || [];
        const updated = current.filter((v) => v !== value);
        onFilterChange(columnKey, updated);
    }, [filters, onFilterChange]);

    const hasActiveFilters = useMemo(() => {
        return Object.values(filters || {}).some((arr) => arr && arr.length > 0);
    }, [filters]);

    if (!data || data.length === 0) return null;

    return (
        <>
            {/* Active filter chips */}
            {showFilters && hasActiveFilters ? (
                <ActiveFilterBar
                    filters={filters}
                    columnLabels={columnLabels}
                    onRemoveFilter={handleRemoveFilter}
                    onClearAll={onClearFilters}
                />
            ) : null}

            <div className="ap-table-container">
                <div className="dp-table-wrap">
                    <table className="dp-table">
                        <thead className="dp-thead">
                            <tr>
                                {activeCols.map((col) => (
                                    <th key={col.key} scope="col" className="ap-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                                        <div className="ap-th__content">
                                            {col.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => onSort(col.key)}
                                                    className="ap-th__sort-btn"
                                                    aria-label={`Sort by ${col.label}`}
                                                >
                                                    {col.label}
                                                    {sortColumn === col.key ? (
                                                        <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                            {sortOrder === 'asc'
                                                                ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                                : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                                            }
                                                        </svg>
                                                    ) : null}
                                                </button>
                                            ) : (
                                                <span>{col.label}</span>
                                            )}

                                            {showFilters && col.filterable ? (
                                                <FilterPopover
                                                    columnKey={col.key}
                                                    label={col.label}
                                                    values={getFilterValues(col.key)}
                                                    selectedValues={filters?.[col.key] || []}
                                                    onChange={onFilterChange}
                                                />
                                            ) : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, idx) => (
                                <tr key={row._rowId || idx} className="dp-tr">
                                    {activeCols.map((col) => (
                                        <td key={col.key} className="dp-td">
                                            {renderCell(row, col.key, tzOffset)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default WellnessTable;
